import { useEffect, useState } from 'react';
import { Card, IndexTable, Text, Button, InlineStack, EmptyState, Banner, Link, Modal } from '@shopify/polaris';
import { api, formatEur, formatDateTime } from './shared.js';

export default function SavedEstimatesTab({ refreshKey, onOpen, notify, tablesAvailable }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await api('/estimates'));
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const open = async (id) => {
    try {
      onOpen(await api(`/estimates/${id}`));
    } catch (err) {
      notify(err.message, true);
    }
  };

  const remove = async () => {
    try {
      await api(`/estimates/${toDelete.EstimateID}`, { method: 'DELETE' });
      notify('Estimate deleted');
      setToDelete(null);
      load();
    } catch (err) {
      notify(err.message, true);
    }
  };

  if (!tablesAvailable) {
    return <Banner tone="warning">Saving estimates needs the database migration server/sql/create_cabinet_estimator_tables.sql.</Banner>;
  }

  return (
    <Card padding="0">
      {error && <Banner tone="critical">{error}</Banner>}
      <IndexTable
        resourceName={{ singular: 'estimate', plural: 'estimates' }}
        itemCount={rows.length}
        loading={loading}
        selectable={false}
        emptyState={
          <EmptyState heading="No saved estimates yet" image="">
            Estimates you save on the Estimate tab show up here.
          </EmptyState>
        }
        headings={[
          { title: 'Name' },
          { title: 'Project' },
          { title: 'Supplier price', alignment: 'end' },
          { title: 'Our price', alignment: 'end' },
          { title: 'By' },
          { title: 'Last change' },
          { title: '' }
        ]}
      >
        {rows.map((row, index) => (
          <IndexTable.Row id={String(row.EstimateID)} key={row.EstimateID} position={index}>
            <IndexTable.Cell>
              <Button variant="plain" onClick={() => open(row.EstimateID)}>
                {row.Name}
              </Button>
            </IndexTable.Cell>
            <IndexTable.Cell>
              {row.ProjectID && row.Projectnummer ? (
                <Link url={`/projects/${row.ProjectID}`} removeUnderline>
                  {row.Projectnummer} {row.Projectnaam ? `· ${row.Projectnaam}` : ''}
                </Link>
              ) : (
                <Text as="span" tone="subdued">
                  —
                </Text>
              )}
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {formatEur(row.SupplierTotal)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
              <Text as="span" alignment="end" numeric>
                {formatEur(row.SellingTotal)}
              </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{row.CreatedByName || '—'}</IndexTable.Cell>
            <IndexTable.Cell>{formatDateTime(row.UpdatedAt || row.CreatedAt)}</IndexTable.Cell>
            <IndexTable.Cell>
              <InlineStack gap="200" align="end">
                <Button size="slim" onClick={() => open(row.EstimateID)}>
                  Open
                </Button>
                <Button size="slim" tone="critical" variant="plain" onClick={() => setToDelete(row)}>
                  Delete
                </Button>
              </InlineStack>
            </IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
      <Modal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        title="Delete estimate?"
        primaryAction={{ content: 'Delete', destructive: true, onAction: remove }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setToDelete(null) }]}
      >
        <Modal.Section>
          <Text as="p">“{toDelete?.Name}” will be deleted. This cannot be undone.</Text>
        </Modal.Section>
      </Modal>
    </Card>
  );
}
